import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { AppProvider, useApp } from '@/contexts/AppContext';
import { ToastProvider, useToast } from '@/contexts/ToastContext';
import { registerSafeWriteToast } from '@/utils/supabaseWrite';
import { t as tRaw } from '@/locales/strings';
import { useEdgeSwipeBack } from '@/hooks/useEdgeSwipeBack';
import ErrorBoundary from '@/components/ErrorBoundary';
import ScreenBoundary from '@/components/ScreenBoundary';
import OnboardingGate from '@/components/OnboardingGate';
import PushLessonPreview from '@/components/onboarding/PushLessonPreview';
import LanguageSelector from '@/components/onboarding/LanguageSelector';
import ConsentMessage from '@/components/onboarding/ConsentMessage';
// OnboardingFlow is still imported but NO LONGER used as a gate. It
// runs on demand from WeekHome's "Start Here" CTA as a full-screen
// overlay. See src/components/training/WeekHome.jsx.
import LoadingScreen from '@/components/ui/LoadingScreen';
import OfflineBanner from '@/components/ui/OfflineBanner';
import ServiceWorkerUpdater from '@/components/ui/ServiceWorkerUpdater';
import InstallPrompt from '@/components/ui/InstallPrompt';
import AuthScreen from '@/components/auth/AuthScreen';
import MembershipGate from '@/components/auth/MembershipGate';
import WeekHome from '@/components/training/WeekHome';
// Secondary routes get code-split so the initial bundle doesn't
// ship their ~400kb of inline SVG + locale data until the user
// actually navigates. Keeps the first-paint budget for the home
// screen tight.
const MethodJourney = lazy(() => import('@/components/method/MethodJourney'));
const PhaseDetail = lazy(() => import('@/components/method/PhaseDetail'));
const WeekDetail = lazy(() => import('@/components/method/WeekDetail'));
import PathBOrientation from '@/components/training/PathBOrientation';
import SessionScreen from '@/components/training/SessionScreen';
import SessionComplete from '@/components/training/SessionComplete';
import SessionFeeling from '@/components/training/SessionFeeling';
import DailyReflection from '@/components/training/DailyReflection';
const PhysioSummary = lazy(() => import('@/components/training/PhysioSummary'));
const Profile        = lazy(() => import('@/components/training/Profile'));
const Progress       = lazy(() => import('@/components/training/Progress'));
const CalendarScreen = lazy(() => import('@/components/training/CalendarScreen'));
const BodyTabHome    = lazy(() => import('@/components/training/BodyTabHome'));
const TrainTabHome   = lazy(() => import('@/components/training/TrainTabHome'));
const DailySymptomLog = lazy(() => import('@/components/tracker/DailySymptomLog'));
const CalendarHistory = lazy(() => import('@/components/tracker/CalendarHistory'));
import PrivacyPolicy from '@/components/legal/PrivacyPolicy';
import TermsOfService from '@/components/legal/TermsOfService';
const PrivacyAtAGlance = lazy(() => import('@/components/legal/PrivacyAtAGlance'));
const LearningList  = lazy(() => import('@/components/learning/LearningList'));
const ArticleDetail = lazy(() => import('@/components/learning/ArticleDetail'));
const Research      = lazy(() => import('@/components/learning/Research'));
const FoundationLessonsList = lazy(() => import('@/components/learning/FoundationLessonsList'));
const AnatomyPrimer = lazy(() => import('@/components/learning/AnatomyPrimer'));
const BodyLetter    = lazy(() => import('@/components/training/BodyLetter'));
const Stats         = lazy(() => import('@/components/training/Stats'));
// Public clinician portal. Lives OUTSIDE the auth + onboarding gates so
// a physio / GP holding a patient-issued share code + password can read
// the read-only report without an app account. Lazy-loaded so the
// clinician chunk never ships to logged-in patients.
const ClinicianGate = lazy(() => import('@/components/clinician/ClinicianGate'));
// Public clinician landing page. Sits OUTSIDE the auth + onboarding
// gates so a physio / GP visiting a link from a referral, conference
// flyer, or patient handover can read the page without an account.
// Lazy-loaded so logged-in patients never pay for the chunk.
const ForClinicians = lazy(() => import('@/components/clinician/ForClinicians'));
// Public Class 1 homework. Students from the intro lesson arrive from
// bebold.au/intro without an account, so it sits OUTSIDE the auth +
// onboarding gates and runs the real RingPlayer.
const Class1Homework = lazy(() => import('@/components/homework/Class1Homework'));
// Public hold test (/teste in Portuguese, /hold-test in English). Anyone
// with the link can time how long she holds a squeeze, so it also sits
// OUTSIDE the auth + onboarding gates.
const HoldTest = lazy(() => import('@/components/homework/HoldTest'));
// Public relax lesson for pain or tension during sex (/relaxar, /relax) and
// the "Not sure where to start?" quiz (/comecar, /start-here). Same pattern.
const RelaxLesson = lazy(() => import('@/components/homework/RelaxLesson'));
const StartQuiz = lazy(() => import('@/components/homework/StartQuiz'));

// Bridges safeWrite's module-level dispatcher into the React toast
// context. Mounted as a sibling of AppShell so it has access to both
// useToast (for showing) and useApp (for the active language). Renders
// nothing.
function SafeWriteToastBridge() {
  const { showToast } = useToast();
  const { lang } = useApp();
  useEffect(() => {
    registerSafeWriteToast(({ kind }) => {
      if (kind === 'offline') {
        showToast({ message: tRaw('toast_saved_offline', lang), tone: 'warn', duration: 4500 });
      } else if (kind === 'online') {
        showToast({ message: tRaw('toast_back_online', lang), tone: 'success', duration: 2500 });
      }
    });
    return () => registerSafeWriteToast(null);
  }, [showToast, lang]);
  return null;
}

function AppShell() {
  const { user, userProgress, loading, patchProgress, passwordRecovery, profile } = useApp();

  // Public /for-clinicians landing page short-circuits every auth +
  // onboarding gate. A clinician arrives without a Supabase session;
  // AppShell would otherwise bounce her to AuthScreen. Match BEFORE
  // /clinician so the longer prefix wins (startsWith would otherwise
  // route /for-clinicians into the share-portal gate by accident if
  // we ever loosen the pattern).
  if (
    typeof window !== 'undefined' &&
    /^\/(teste|hold-test)\/?$/.test(window.location.pathname)
  ) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <HoldTest />
      </Suspense>
    );
  }

  if (
    typeof window !== 'undefined' &&
    /^\/(relaxar|relax)\/?$/.test(window.location.pathname)
  ) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <RelaxLesson />
      </Suspense>
    );
  }

  if (
    typeof window !== 'undefined' &&
    /^\/(comecar|start-here)\/?$/.test(window.location.pathname)
  ) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <StartQuiz />
      </Suspense>
    );
  }

  if (
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/homework/class-1')
  ) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <Class1Homework />
      </Suspense>
    );
  }

  if (
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/for-clinicians')
  ) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <ForClinicians />
      </Suspense>
    );
  }

  // Public /clinician portal short-circuits every auth + onboarding
  // gate. A clinician holds a patient-issued code + password, not a
  // Supabase session, so AppShell would otherwise bounce her to login.
  // Match by pathname directly (not Router) so this works even before
  // AppRoutes mounts.
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/clinician')
  ) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <ClinicianGate />
      </Suspense>
    );
  }

  // Dev-only: the /dev/push-lesson preview short-circuits every auth
  // and onboarding gate so a Playwright 375px pass can mount the Push
  // lesson directly. Only active when import.meta.env.DEV is true; the
  // block is tree-shaken out of production builds by the guard.
  if (
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/dev/push-lesson')
  ) {
    return <AppRoutes />;
  }

  if (loading) return <LoadingScreen />;

  // Password recovery click-through (Supabase fires PASSWORD_RECOVERY
  // on the email link). Render AuthScreen so the user can set a new
  // password before the rest of the app unlocks. Takes precedence over
  // every other gate because the link lands with an active session.
  if (passwordRecovery) return <AuthScreen />;

  // Not authenticated: render the React AuthScreen (login / signup /
  // forgot-password) instead of falling through to the vanilla
  // s-login shell. Vanilla DOM is still present but AuthScreen strips
  // its .on class on mount so it never paints.
  if (!user) return <AuthScreen />;

  // Membership gate: only block when profiles.is_active is explicitly
  // false. null (profile not loaded, timeout, missing row) falls
  // through — the paywall is a soft gate and we never strand a paying
  // user on a network hiccup. ?dev=true URL flag bypasses the paywall
  // for local testing, mirroring the vanilla dev bypass.
  const devBypass = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('dev') === 'true';
  if (!devBypass && profile && profile.is_active === false) {
    return <MembershipGate />;
  }

  // Gate 1: Language selector (first screen after SSO, shows once).
  //
  // onComplete flips the gate optimistically via patchProgress instead
  // of refetching from Supabase. LanguageSelector has already called
  // safeWrite() to persist `language_selected: true` to the bb7 blob;
  // the in-memory patch means the AppShell advances on the same tick
  // even if the Supabase write is slow, hits RLS, or races the refetch
  // (which was the cause of the "click does nothing" symptom — the
  // refetch-based version left the gate open when the write had not
  // yet landed in user_progress, so AppShell re-rendered the same
  // LanguageSelector and the user was stuck).
  if (!userProgress.language_selected) {
    return <LanguageSelector onComplete={() => patchProgress({ language_selected: true })} />;
  }

  // Gate 2: Consent required before the rest of the app. Same optimistic
  // patchProgress pattern as Gate 1 — ConsentMessage fires safeWrite()
  // in the background, and AppShell flips the gate on the same tick so
  // a slow or failing Supabase write can't strand the user on the
  // consent screen.
  if (!userProgress.consent_complete) {
    return <ConsentMessage onComplete={() => patchProgress({ consent_complete: true })} />;
  }

  // Onboarding is NO LONGER a gate. Users past consent go straight to
  // the full app. WeekHome's session card renders a "Start Here" CTA
  // that triggers OnboardingFlow as an overlay on demand, and flips to
  // "Start" once onboarding_complete is true.
  return <AppRoutes />;
}

function AppRoutes() {
  const { lang } = useApp();
  const location = useLocation();
  const navType = useNavigationType();
  // iOS-style edge-swipe-from-left to navigate back. No-ops on
  // top-level tabs (/home, /calendar, /learning, /profile) since
  // there's no history to pop back to in those cases.
  useEdgeSwipeBack();
  // Idle-prefetch the BottomNav routes so the first tap into /calendar
  // / /progress / /profile / /learning doesn't pay a full chunk fetch.
  // Fires once, after first paint, only if requestIdleCallback exists
  // (Safari < 17 silently skips). Each import is fire-and-forget; an
  // import that fails just means the user pays the original price on
  // tap.
  useEffect(() => {
    if (typeof requestIdleCallback !== 'function') return undefined;
    const handle = requestIdleCallback(() => {
      import('@/components/training/CalendarScreen').catch(() => {});
      import('@/components/training/Progress').catch(() => {});
      import('@/components/training/Profile').catch(() => {});
      import('@/components/learning/LearningList').catch(() => {});
    }, { timeout: 4000 });
    return () => {
      try { cancelIdleCallback?.(handle); } catch { /* older Safari */ }
    };
  }, []);
  // Keying the wrapper on pathname remounts it on every route change,
  // which re-triggers the motionPageFade animation. No extra deps; the
  // downside is a new element subtree each navigation, which is fine
  // for the handful of routes we have.
  //
  // Directionality: POP = back (browser back / navigate(-1)), so the
  // incoming screen slides in from the left. PUSH / REPLACE = forward,
  // slides in from the right. First render (REPLACE on a fresh session)
  // falls through to the plain fade so the app doesn't "enter from the
  // right" cold.
  const dirClass =
    navType === 'POP' ? 'motion-page--back'
    : navType === 'PUSH' ? 'motion-page--forward'
    : 'motion-page';
  // The `transform` on .motion-page--forward / .motion-page--back
  // (used for the slide-in/slide-back animation) creates a containing
  // block for any `position: fixed` descendant. Profile, Progress,
  // CalendarScreen, LearningList and BottomNav all use `position:
  // fixed; inset: 0` — so their rectangle is resolved against THIS
  // wrapper, not the viewport. With only `minHeight: '100%'` the
  // wrapper collapsed to 0 (its parent `#root` is a flex-shrink
  // sibling of legacy `.app` with no explicit height), leaving every
  // fixed-positioned screen invisible and revealing the vanilla
  // `#s-login` underneath when a user tabbed away from /home. Pinning
  // `minHeight: 100dvh` guarantees the containing block is always
  // viewport-sized, so fixed children render correctly across every
  // route. (Chrome 108+, Safari 15.4+, Firefox 101+ all support dvh.)
  return (
    <div
      key={location.pathname}
      className={dirClass}
      style={{ minHeight: '100dvh' }}
    >
      <Suspense fallback={<LoadingScreen />}>
      <ScreenBoundary lang={lang}>
      <Routes location={location}>
        {/* Training routes */}
        <Route path="/home" element={<WeekHome />} />
        <Route path="/method" element={<MethodJourney />} />
        <Route path="/method/:phaseKey" element={<PhaseDetail />} />
        <Route path="/method/:phaseKey/week/:weekNumber" element={<WeekDetail />} />
        <Route path="/path-b-orientation" element={<PathBOrientation />} />
        <Route path="/session" element={<SessionScreen />} />
        <Route path="/session-feeling" element={<SessionFeeling />} />
        <Route path="/complete" element={<SessionComplete />} />
        <Route path="/reflection" element={<DailyReflection />} />

        {/* /calendar is reachable via deep links and AppGuide only;
            the bottom nav surfaces /body for the cycle + grid and
            /progress for training history. The route renders just
            the calendar grid (cycle arc moved onto /body). */}
        <Route path="/calendar" element={<CalendarScreen />} />
        {/* Train tab landing — IA migration step 6 follow-up.
            Replaces the stop-gap routing of the Train bottom-nav
            button straight to /method with a dedicated hub
            (week-at-a-glance + Method + Foundation + Progress
            + Path B orientation). /method itself is unchanged. */}
        <Route path="/train" element={<TrainTabHome />} />
        {/* Body tab. The cycle arc + tappable calendar grid + libido
            quick-log live here, gated by life stage. */}
        <Route path="/body" element={<BodyTabHome />} />
        {/* Health Tracker daily symptom log (structured, scored entries). */}
        <Route path="/log" element={<DailySymptomLog />} />
        {/* Health Tracker month history, colour-coded by intensity. */}
        <Route path="/history" element={<CalendarHistory />} />
        <Route path="/progress" element={<Progress />} />
        <Route path="/profile" element={<Profile />} />

        {/* Legal pages (reachable from Profile) */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/privacy-at-a-glance" element={<PrivacyAtAGlance />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* Physio summary (reachable from Profile). Printable one-page
            clinical view; the route itself renders the save/back toolbar. */}
        <Route path="/physio-summary" element={<PhysioSummary />} />

        {/* Learning — pelvic floor education library reachable from
            Profile. /learning lists six articles; /learning/:id renders
            a single article. Unknown ids redirect back to the list. */}
        <Route path="/learning" element={<LearningList />} />
        <Route path="/learning/:id" element={<ArticleDetail />} />
        {/* Evidence library: peer-reviewed citations behind the
            method. Reachable from Profile > Evidence. */}
        <Route path="/research" element={<Research />} />

        {/* Anatomy primer: where the pelvic floor lives in the
            body, with three layer chips. Reachable from the
            AppGuide and from FoundationLesson. */}
        <Route path="/anatomy" element={<AnatomyPrimer />} />

        {/* Foundation lessons: revisit any of the nine onboarding
            lessons. Reachable from Profile > Foundation lessons.
            Mounts FoundationLesson in revisit mode so reopening it
            doesn't rewrite onboarding flags. */}
        <Route path="/foundation" element={<FoundationLessonsList />} />

        {/* Body Letter — a monthly, print-ready reflection page.
            Premium-feel surface: addressed to the user by name,
            pulls from AppContext only (no extra Supabase fetch). */}
        <Route path="/body-letter" element={<BodyLetter />} />

        {/* Stats — 90-day heatmap + chapter-arc breakdown + stat
            grid. Data lives in localStorage bb7 (trainedDays) +
            userProgress, so no extra Supabase fetch. */}
        <Route path="/stats" element={<Stats />} />

        {/* Dev-only preview for screenshot QA. Mounts FoundationLesson
            directly at ?screen=N without the auth + onboarding gates
            so a Playwright 375px visual pass can cover screens 10 / 11
            in isolation. Tree-shaken out of production builds by the
            DEV guard. */}
        {import.meta.env.DEV && (
          <Route path="/dev/push-lesson" element={<PushLessonPreview />} />
        )}

        {/* Catch-all: OnboardingGate checks flags and routes */}
        <Route path="*" element={<OnboardingGate />} />
      </Routes>
      </ScreenBoundary>
      </Suspense>
    </div>
  );
}

export default function App() {
  // ErrorBoundary wraps AppProvider so a crash inside the context
  // initializer (e.g. a malformed bb7 blob in localStorage) still
  // surfaces the cream fallback instead of a blank screen.
  // ToastProvider sits inside AppProvider so the bridge below can
  // read `lang` from useApp(), and renders the toast portal once at
  // the root so any screen can show toasts without mounting its own.
  return (
    <ErrorBoundary>
      <AppProvider>
        <ToastProvider>
          <SafeWriteToastBridge />
          {/* Ambient PWA helpers — fixed-position, aria-live, portal
              themselves to the top of the viewport. Mounted inside
              AppProvider so they can read `lang` but OUTSIDE AppShell
              so they survive every route change and gate transition
              (auth, membership, onboarding). Each component
              self-gates (no network = banner; SW update available =
              toast; install prompt only after ≥1 completed session)
              and renders null otherwise. */}
          <OfflineBanner />
          <ServiceWorkerUpdater />
          <InstallPrompt />
          <AppShell />
        </ToastProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}
